import { useCallback, useEffect, useState } from 'react';
import type {
  BroadcastListItem,
  BroadcastTargetType,
  DevicePlatform,
  SendBroadcastRequest,
} from '@shared/types';
import PageHeader from '../../components/PageHeader';
import PageSection from '../../components/PageSection';
import Form, { Field } from '../../components/Form';
import Alert from '../../components/Alert';
import Pill, { type PillVariant } from '../../components/Pill';
import { listBroadcasts, sendBroadcast } from '../../services/broadcasts';
import { ApiError } from '../../services/api';

const TITLE_MAX = 64;
const BODY_MAX = 240;

function statusVariant(status: BroadcastListItem['status']): PillVariant {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'danger';
    case 'sending':
      return 'info';
    default:
      return 'neutral';
  }
}

export default function BroadcastsPage() {
  const [items, setItems] = useState<BroadcastListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState<BroadcastTargetType>('all');
  const [platform, setPlatform] = useState<DevicePlatform>('ios');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listBroadcasts({ page: 1, limit: 50 });
      setItems(res.items);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? err.message : 'Could not load broadcast history.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const canSubmit =
    title.trim().length > 0 &&
    body.trim().length > 0 &&
    title.length <= TITLE_MAX &&
    body.length <= BODY_MAX &&
    !submitting;

  async function handleSubmit() {
    setSubmitError(null);
    setSubmitSuccess(null);
    setSubmitting(true);
    const req: SendBroadcastRequest = {
      title: title.trim(),
      body: body.trim(),
      targetType,
      ...(targetType === 'platform' ? { platform } : {}),
    };
    try {
      const created = await sendBroadcast(req);
      setSubmitSuccess(
        `Sent to ${created.recipientCount} device${created.recipientCount === 1 ? '' : 's'} · ${created.successCount} delivered, ${created.failureCount} failed.`
      );
      setTitle('');
      setBody('');
      await load();
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : 'Broadcast failed to send.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="broadcasts-page">
      <PageHeader
        title="Broadcast Notifications"
        subtitle="Send a push notification to every user or a specific platform."
      />

      <PageSection title="New broadcast" testId="broadcasts-compose">
        <Form onSubmit={handleSubmit} testId="broadcasts-form">
          <Field
            label="Title"
            htmlFor="broadcast-title"
            hint={`${title.length}/${TITLE_MAX}`}
            required
          >
            <input
              id="broadcast-title"
              data-testid="broadcasts-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX))}
              maxLength={TITLE_MAX}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              disabled={submitting}
            />
          </Field>

          <Field
            label="Body"
            htmlFor="broadcast-body"
            hint={`${body.length}/${BODY_MAX}`}
            required
          >
            <textarea
              id="broadcast-body"
              data-testid="broadcasts-body"
              value={body}
              onChange={(e) => setBody(e.target.value.slice(0, BODY_MAX))}
              maxLength={BODY_MAX}
              rows={3}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
              disabled={submitting}
            />
          </Field>

          <Field label="Audience" htmlFor="broadcast-target">
            <select
              id="broadcast-target"
              data-testid="broadcasts-target"
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as BroadcastTargetType)}
              className="border border-slate-300 rounded px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="all">All users</option>
              <option value="platform">Platform</option>
            </select>
          </Field>

          {targetType === 'platform' && (
            <Field label="Platform" htmlFor="broadcast-platform">
              <select
                id="broadcast-platform"
                data-testid="broadcasts-platform"
                value={platform}
                onChange={(e) => setPlatform(e.target.value as DevicePlatform)}
                className="border border-slate-300 rounded px-3 py-2 text-sm"
                disabled={submitting}
              >
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
            </Field>
          )}

          {submitError && (
            <Alert tone="error" testId="broadcasts-submit-error">
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert tone="success" testId="broadcasts-submit-success">
              {submitSuccess}
            </Alert>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              data-testid="broadcasts-submit"
              disabled={!canSubmit}
              className="rounded bg-slate-900 text-white text-sm px-4 py-2 disabled:opacity-50"
            >
              {submitting ? 'Sending…' : 'Send broadcast'}
            </button>
          </div>
        </Form>
      </PageSection>

      <PageSection title="History" testId="broadcasts-history">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {loadError && (
          <Alert tone="error" testId="broadcasts-load-error">
            {loadError}
          </Alert>
        )}
        {!loading && !loadError && items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="broadcasts-empty">
            No broadcasts yet.
          </p>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm" data-testid="broadcasts-table">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Audience</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Delivered</th>
                <th className="py-2 pr-4">Sent by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} className="border-t border-slate-200">
                  <td className="py-2 pr-4 text-slate-600">
                    {new Date(b.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-medium text-slate-900">
                    {b.title}
                  </td>
                  <td className="py-2 pr-4">
                    <Pill variant="neutral">
                      {b.targetType === 'all'
                        ? 'All'
                        : `Platform · ${b.platform ?? '?'}`}
                    </Pill>
                  </td>
                  <td className="py-2 pr-4">
                    <Pill variant={statusVariant(b.status)}>{b.status}</Pill>
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-slate-700">
                    {b.successCount}/{b.recipientCount}
                    {b.failureCount > 0 && (
                      <span className="ml-1 text-red-700">
                        ({b.failureCount} failed)
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-slate-600">
                    {b.createdBy.username}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PageSection>
    </div>
  );
}
